import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { HelpCircle, Loader2, Clock, CheckCircle2, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VendorQuestionsTab = ({ onPendingChange }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submittingId, setSubmittingId] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vendors/me/questions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = r.data || [];
      setQuestions(data);
      onPendingChange?.(data.filter((q) => q.status === 'pending').length);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const submitAnswer = async (questionId) => {
    const content = (answers[questionId] || '').trim();
    if (!content) {
      toast.error(T('Votre réponse est vide', 'Your answer is empty'));
      return;
    }
    setSubmittingId(questionId);
    try {
      await axios.post(
        `${API}/qna/${questionId}/answer`,
        { answer: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(T('Réponse publiée sur la fiche produit ✓', 'Answer published on the product page ✓'));
      setAnswers((a) => ({ ...a, [questionId]: '' }));
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur', 'Error'));
    } finally {
      setSubmittingId(null);
    }
  };

  const pendingCount = questions.filter((q) => q.status === 'pending').length;

  return (
    <Card data-testid="vendor-questions-tab">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-[#0F4C5C]" />
          {T('Questions clients', 'Customer questions')}
        </CardTitle>
        {pendingCount > 0 && (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="vendor-questions-pending-count">
            {pendingCount} {T('en attente', 'pending')}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {T('Aucune question pour le moment', 'No questions yet')}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {questions.map((q) => (
              <div
                key={q.id}
                className={`border rounded-xl p-5 ${q.status === 'pending' ? 'border-amber-200 bg-amber-50/40' : 'border-[#0F4C5C]/10 bg-white'}`}
                data-testid={`vendor-question-${q.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#0F4C5C]/70 uppercase tracking-wide mb-1">{q.product_name}</p>
                    <p className="font-medium text-[#0F4C5C]">{q.question}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {q.user_name} · {new Date(q.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {q.status === 'pending' ? (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 whitespace-nowrap">
                      <Clock className="w-3 h-3 mr-1" />
                      {T('À répondre', 'To answer')}
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 whitespace-nowrap">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {T('Publiée', 'Published')}
                    </Badge>
                  )}
                </div>

                {q.answer && (
                  <div className="mt-3 ml-2 pl-4 border-l-2 border-[#0F4C5C]/30 text-sm text-[#0F4C5C]/90">
                    {q.answer.content}
                  </div>
                )}

                <div className="mt-4 flex gap-2 items-start">
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                    maxLength={2000}
                    rows={2}
                    placeholder={q.answer
                      ? T('Modifier votre réponse…', 'Edit your answer…')
                      : T('Votre réponse publique…', 'Your public answer…')}
                    className="flex-1"
                    data-testid={`answer-input-${q.id}`}
                  />
                  <Button
                    onClick={() => submitAnswer(q.id)}
                    disabled={submittingId === q.id}
                    className="btn-primary"
                    data-testid={`answer-submit-${q.id}`}
                  >
                    {submittingId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
